import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, RefreshCw } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useTasksByRelated } from "../hooks/useWorkTasks";
import { TaskStatusPill } from "./TaskStatusPill";
import { TaskPriorityBadge } from "./TaskPriorityBadge";
import { TaskDueDatePill } from "./TaskDueDatePill";
import { TaskAssigneeAvatar } from "./TaskAssigneeAvatar";
import { QuickAddTaskModal } from "./QuickAddTaskModal";
import type { WorkTaskDto, WorkTaskPriority, WorkTaskRelatedEntityType } from "@/api/workTasks";

interface Props {
  relatedEntityType: WorkTaskRelatedEntityType;
  relatedEntityId: string;
  /** Override the default `+ Add task linked to this {entity}` button label. */
  addButtonLabel?: string;
}

const NOUN: Record<WorkTaskRelatedEntityType, string> = {
  None: "entity",
  Deal: "deal",
  Broker: "broker",
  Report: "report",
  Loan: "loan",
  Document: "document",
  Lead: "lead",
  Person: "person",
  Entity: "entity",
  Business: "business",
  SpvPool: "SPV pool",
  Spv: "SPV trust",
  Investor: "investor",
  IndentureTrustee: "indenture trustee",
  TrustCompany: "trust company",
  PlacementAgent: "placement agent",
  BackupServicer: "backup servicer",
  ReunderwritingCase: "reunderwriting case",
  Vendor: "vendor",
  Tps: "TPS provider",
  LocalEnforcer: "local enforcer",
  PortalUser: "portal account",
  Brokerage: "brokerage",
  Borrower: "borrower",
  Seller: "seller",
  // Buyer CRM (PR 2a). NOUN is an exhaustive Record over the Tasks union, so
  // omitting this is a typecheck failure rather than a runtime fallback.
  BuyerLead: "buyer",
  // Buyer programme (PR 2). "submission" rather than "buyer submission":
  // the noun is rendered inline in a sentence about the record already on
  // screen, so the buyer half is redundant there.
  Listing: "listing",
  BuyerSubmission: "submission",
};

const PRIORITY_RANK: Record<WorkTaskPriority, number> = {
  Critical: 4,
  High: 3,
  Medium: 2,
  Low: 1,
};

const MAX_ROWS = 200;

function compareOpenTasks(a: WorkTaskDto, b: WorkTaskDto): number {
  // Open: due-date ascending (nulls last), then priority descending.
  const ad = a.dueDate ? Date.parse(a.dueDate) : Number.POSITIVE_INFINITY;
  const bd = b.dueDate ? Date.parse(b.dueDate) : Number.POSITIVE_INFINITY;
  if (ad !== bd) return ad - bd;
  return PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority];
}

function compareClosedTasks(a: WorkTaskDto, b: WorkTaskDto): number {
  // Closed: completion-date descending (nulls last).
  const ac = a.completedDate ? Date.parse(a.completedDate) : Number.NEGATIVE_INFINITY;
  const bc = b.completedDate ? Date.parse(b.completedDate) : Number.NEGATIVE_INFINITY;
  return bc - ac;
}

/**
 * Tasks Integration Standard §5 / §6.3 — embedded "Tasks" panel for
 * an entity's detail page. Wraps the central list-by-related query
 * and the QuickAddTaskModal so that every entity surface renders the
 * same list shape, sort, filter, and create-affordance.
 */
export function TaskListMini({ relatedEntityType, relatedEntityId, addButtonLabel }: Props) {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("Tasks.Create");
  const q = useTasksByRelated(relatedEntityType, relatedEntityId);

  const [modalOpen, setModalOpen] = useState(false);
  const [showCompleted, setShowCompleted] = useState(true);

  const noun = NOUN[relatedEntityType];
  const buttonLabel = addButtonLabel ?? `Add task linked to this ${noun}`;

  const tasks = q.data ?? [];
  const openTasks = useMemo(
    () => tasks.filter((t) => t.status !== "Done").sort(compareOpenTasks),
    [tasks],
  );
  const closedTasks = useMemo(
    () => tasks.filter((t) => t.status === "Done").sort(compareClosedTasks),
    [tasks],
  );
  const visibleTasks = useMemo(() => {
    const combined = showCompleted ? [...openTasks, ...closedTasks] : openTasks;
    return combined.slice(0, MAX_ROWS);
  }, [openTasks, closedTasks, showCompleted]);

  const openCount = openTasks.length;
  const hasCompleted = closedTasks.length > 0;

  return (
    <Card>
      <div className="flex items-center justify-between mb-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
            Linked
          </div>
          <h3 className="font-display font-bold text-h3 text-onyx m-0">
            Tasks
            {openCount > 0 && (
              <span
                className="ml-space-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 px-space-2 rounded-pill bg-gold text-onyx font-display text-[11px] font-bold align-middle"
                aria-label={`${openCount} open ${openCount === 1 ? "task" : "tasks"}`}
              >
                {openCount}
              </span>
            )}
          </h3>
        </div>
        <Button
          size="sm"
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={!canCreate}
          title={canCreate ? undefined : "You do not have permission to create tasks."}
          aria-label={buttonLabel}
        >
          <Plus size={14} aria-hidden /> Add task
        </Button>
      </div>

      {hasCompleted && (
        <div className="flex items-center justify-end mb-space-2">
          <label className="inline-flex items-center gap-space-2 text-small text-gray-mid cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Show completed
          </label>
        </div>
      )}

      {q.isLoading && (
        <ul
          className="flex flex-col gap-space-2 m-0 p-0 list-none"
          aria-label="Loading tasks"
        >
          {[0, 1, 2].map((i) => (
            <li
              key={i}
              className="h-9 rounded-md bg-white-warm animate-pulse"
              aria-hidden
            />
          ))}
        </ul>
      )}

      {q.isError && !q.isLoading && (
        <div
          role="alert"
          className="flex items-center justify-between gap-space-3 p-space-3 rounded-md bg-status-error/10 text-small text-status-error"
        >
          <span>Could not load tasks for this {noun}.</span>
          <Button size="sm" variant="tertiary" type="button" onClick={() => q.refetch()}>
            <RefreshCw size={14} aria-hidden /> Retry
          </Button>
        </div>
      )}

      {q.isSuccess && visibleTasks.length === 0 && (
        <div className="text-small text-gray-mid">
          No tasks linked to this {noun} yet.
        </div>
      )}

      {visibleTasks.length > 0 && (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {visibleTasks.map((t) => (
            <li key={t.id}>
              <Link
                to={`/tasks/${t.id}`}
                className="flex items-center justify-between gap-space-3 p-space-2 -mx-space-2 rounded-md hover:bg-white-warm"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-small text-onyx truncate">{t.title}</div>
                </div>
                <div className="flex items-center gap-space-2 shrink-0">
                  <TaskStatusPill status={t.status} percent={t.progressPercent} />
                  <TaskPriorityBadge
                    priority={t.priority}
                    className="hidden lg:inline-flex"
                  />
                  <TaskAssigneeAvatar assignee={t.assignee} showName={false} />
                  {t.status !== "Done" && t.dueDate && (
                    <TaskDueDatePill dueDate={t.dueDate} />
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <QuickAddTaskModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        prefilledRelatedType={relatedEntityType}
        prefilledRelatedEntityId={relatedEntityId}
        prefilledRelatedEntityLabel={noun}
      />
    </Card>
  );
}
